/**
 * Geometry utility functions for map calculations
 */

/**
 * Calculate distance between two geographic coordinates using Haversine formula
 * @param coord1 - First coordinate [longitude, latitude]
 * @param coord2 - Second coordinate [longitude, latitude]
 * @returns Distance in meters
 */
export const calculateDistance = (
  coord1: number[],
  coord2: number[]
): number => {
  const [lon1, lat1] = coord1
  const [lon2, lat2] = coord2
  const R = 6371000 // Earth's radius in meters

  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLon = ((lon2 - lon1) * Math.PI) / 180

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2)

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

/**
 * Get midpoint between two geographic coordinates
 * @param coord1 - First coordinate [longitude, latitude]
 * @param coord2 - Second coordinate [longitude, latitude]
 * @returns Midpoint coordinate [longitude, latitude]
 */
export const getMidpoint = (coord1: number[], coord2: number[]): number[] => {
  return [(coord1[0] + coord2[0]) / 2, (coord1[1] + coord2[1]) / 2]
}

/**
 * Extract coordinates from GeoJSON geometry
 * @param geoJson - GeoJSON geometry object
 * @returns Array of coordinate arrays
 */
export const extractCoordinates = (geoJson: {
  type: string
  coordinates?: any
  geometry?: any
  features?: any[]
}): number[][][] => {
  // Unwrap Feature to its geometry
  if (geoJson.type === 'Feature') {
    return geoJson.geometry ? extractCoordinates(geoJson.geometry) : []
  }
  // For FeatureCollection, extract from the first feature
  if (geoJson.type === 'FeatureCollection') {
    return geoJson.features?.[0] ? extractCoordinates(geoJson.features[0]) : []
  }
  let coordinates: number[][][] = []
  if (geoJson.type === 'MultiPolygon') {
    coordinates = geoJson.coordinates[0] // Take first polygon of multipolygon
  } else if (geoJson.type === 'Polygon') {
    coordinates = geoJson.coordinates
  }
  return coordinates
}

/**
 * Calculate bearing (direction angle) between two geographic coordinates
 * @param coord1 - First coordinate [longitude, latitude]
 * @param coord2 - Second coordinate [longitude, latitude]
 * @returns Bearing in degrees (0-360, where 0 = North, 90 = East)
 */
export const calculateBearing = (
  coord1: number[],
  coord2: number[]
): number => {
  const [lon1, lat1] = coord1
  const [lon2, lat2] = coord2

  const dLon = ((lon2 - lon1) * Math.PI) / 180
  const lat1Rad = (lat1 * Math.PI) / 180
  const lat2Rad = (lat2 * Math.PI) / 180

  const y = Math.sin(dLon) * Math.cos(lat2Rad)
  const x =
    Math.cos(lat1Rad) * Math.sin(lat2Rad) -
    Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLon)

  const bearing = (Math.atan2(y, x) * 180) / Math.PI
  return (bearing + 360) % 360
}

/**
 * Format distance for display
 * @param meters - Distance in meters
 * @returns Formatted distance string (e.g., "150m" or "1.5km")
 */
export const formatDistance = (meters: number): string => {
  if (!meters) return ''
  if (meters < 1000) return `${Math.round(meters)}m`
  return `${(meters / 1000).toFixed(1)}km`
}

/**
 * Smallest absolute angle (0–180) between two bearings.
 */
const bearingDelta = (a: number, b: number) => {
  let diff = Math.abs(a - b)
  if (diff > 180) diff = 360 - diff
  return diff
}

export interface DimensionLabelFeature {
  type: 'Feature'
  geometry: { type: 'Point'; coordinates: number[] }
  properties: {
    distance: number
    distanceLabel: string
  }
}

export interface BuildDimensionLabelsOptions {
  /** Maximum bearing difference (deg) for two segments to be considered collinear. */
  collinearThreshold?: number
  /** Drop labels whose merged side length is below this many meters. */
  minLabelDistance?: number
  /**
   * Drop groups whose internal segments deviate more than this many degrees
   * from the chord direction — these are the curved arcs of rounded /
   * buffered polygons, not real sides. Opt-in: defaults to no filter so
   * legitimate sides with slightly noisy bearings (coordinate precision)
   * are never lost. Set a value (e.g. 7) when the source polygon is heavily
   * rounded / buffered and you want to suppress arc labels.
   */
  maxChordDeviation?: number
  /** How to format the label text. */
  formatLabel?: (meters: number) => string
}

/**
 * Build dimension labels (one per real side) for the outer ring of a polygon.
 *
 * Handles:
 * - Collinear-but-noisy segments: merged into one label per straight side.
 * - Closed-ring polygons that start mid-side: first/last groups merged.
 * - Rounded / buffered polygons: arc segments filtered out so labels only land
 *   on real straight sides.
 */
export const buildDimensionLabels = (
  outerRing: number[][],
  options: BuildDimensionLabelsOptions = {}
): DimensionLabelFeature[] => {
  const {
    collinearThreshold = 20,
    minLabelDistance = 1,
    maxChordDeviation = Number.POSITIVE_INFINITY,
    formatLabel = (m) => `${m.toFixed(1)}m`,
  } = options

  if (!outerRing || outerRing.length < 2) return []

  type Segment = {
    start: number[]
    end: number[]
    distance: number
    bearing: number
  }

  const segments: Segment[] = []
  for (let i = 0; i < outerRing.length - 1; i++) {
    const start = outerRing[i]
    const end = outerRing[i + 1]
    const distance = calculateDistance(start, end)
    if (distance === 0) continue
    segments.push({
      start,
      end,
      distance,
      bearing: calculateBearing(start, end),
    })
  }

  if (segments.length === 0) return []

  // Group roughly-collinear consecutive segments. Compare each new segment
  // against the FIRST segment's bearing in the group (not the running last)
  // so that gradually-drifting bearings on a rounded corner can't chain
  // together with adjacent straight sides.
  const groups: Segment[][] = []
  let currentGroup: Segment[] = [segments[0]]
  for (let i = 1; i < segments.length; i++) {
    const refBearing = currentGroup[0].bearing
    if (bearingDelta(segments[i].bearing, refBearing) <= collinearThreshold) {
      currentGroup.push(segments[i])
    } else {
      groups.push(currentGroup)
      currentGroup = [segments[i]]
    }
  }
  groups.push(currentGroup)

  // Closed-ring polygons start at an arbitrary vertex. If the ring starts
  // mid-side, the side is split across the last and first groups — merge
  // them back when their reference bearings are collinear.
  if (
    groups.length > 1 &&
    bearingDelta(groups[0][0].bearing, groups[groups.length - 1][0].bearing) <=
      collinearThreshold
  ) {
    const tail = groups.pop() as Segment[]
    groups[0] = [...tail, ...groups[0]]
  }

  return groups
    .map((group) => {
      const totalDistance = group.reduce((sum, seg) => sum + seg.distance, 0)
      const groupStart = group[0].start
      const groupEnd = group[group.length - 1].end

      // Straight side vs. arc: a straight side's segments are all aligned with
      // the chord (start→end). On a rounded corner the segments fan around it,
      // so the maximum deviation from the chord bearing is large.
      let chordDeviation = 0
      if (group.length > 1) {
        const chordBearing = calculateBearing(groupStart, groupEnd)
        for (const seg of group) {
          const dev = bearingDelta(seg.bearing, chordBearing)
          if (dev > chordDeviation) chordDeviation = dev
        }
      }

      return {
        totalDistance,
        chordDeviation,
        midpoint: getMidpoint(groupStart, groupEnd),
      }
    })
    .filter(
      ({ totalDistance, chordDeviation }) =>
        totalDistance >= minLabelDistance && chordDeviation <= maxChordDeviation
    )
    .map(
      ({ totalDistance, midpoint }): DimensionLabelFeature => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: midpoint },
        properties: {
          distance: Number(totalDistance.toFixed(2)),
          distanceLabel: formatLabel(totalDistance),
        },
      })
    )
}
